import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import { ThemeProvider } from './lv/LVTheme';
import { CompanionOverlay, CompanionProvider } from './lv/LVCompanion';
import useActivityPing from './useActivityPing';

// Every route lazy-loads, including the marketing site (Hub/Website/
// TutoringGate) - previously these three were eager imports, so every
// visitor to any page (including a shared /tutoring/sat/formulas link)
// downloaded the whole marketing site's code in the main bundle before
// it could render anything. Performance review finding, roadmap audit.
const Hub = lazy(() => import('./Hub'));
const Website = lazy(() => import('./Website'));
const TutoringGate = lazy(() => import('./TutoringGate'));
const Admin = lazy(() => import('./Admin'));
const StudentPortal = lazy(() => import('./StudentPortal'));
const CourseFeedback = lazy(() => import('./CourseFeedback'));
const Gym = lazy(() => import('./Gym'));
const GymAdmin = lazy(() => import('./GymAdmin'));
const SatAssessment = lazy(() => import('./SatAssessment'));
const SatResultsPage = lazy(() => import('./SatAssessment').then(m => ({ default: m.SatResultsPage })));
const SatLogin = lazy(() => import('./preppeak/PPGuest').then(m => ({ default: m.SatLogin })));
const PPForgotPassword = lazy(() => import('./preppeak/PPGuest').then(m => ({ default: m.PPForgotPassword })));
const PPResetPassword = lazy(() => import('./preppeak/PPGuest').then(m => ({ default: m.PPResetPassword })));
const PPTerms = lazy(() => import('./preppeak/PPLegal').then(m => ({ default: m.PPTerms })));
const PPPrivacy = lazy(() => import('./preppeak/PPLegal').then(m => ({ default: m.PPPrivacy })));
const SatClassified = lazy(() => import('./SatClassified'));
const SatPracticeHub = lazy(() => import('./SatClassified').then(m => ({ default: m.SatPracticeHub })));
const SatDashboard = lazy(() => import('./SatDashboard'));
const SatPath = lazy(() => import('./SatPath'));
const SatPathSkill = lazy(() => import('./SatPathSkill'));
const SatOnboarding = lazy(() => import('./SatOnboarding'));
const SatPlan = lazy(() => import('./SatPlan'));
const SatLearn = lazy(() => import('./SatLearn'));
const SatMasteryMap = lazy(() => import('./SatMasteryMap'));
const SatFormulaReference = lazy(() => import('./SatFormulaReference'));
const SatToolkit = lazy(() => import('./SatToolkit'));
const SatProgress = lazy(() => import('./SatProgress'));
const SatLesson = lazy(() => import('./SatLesson'));
const SatAdminReports = lazy(() => import('./SatAdminReports'));
const SatAdminStudents = lazy(() => import('./SatAdminStudents'));
const SatAdminApplications = lazy(() => import('./SatAdminApplications'));
const AcqApply = lazy(() => import('./acq/AcqApply'));
const SatReviewQuiz = lazy(() => import('./SatReviewQuiz'));
const SatExamLibrary = lazy(() => import('./SatExamLibrary'));
const AdaptiveExamPicker = lazy(() => import('./SatAdaptiveExam').then(m => ({ default: m.AdaptiveExamPicker })));
const SatAdaptiveExamRunner = lazy(() => import('./SatAdaptiveExam'));
const OfficialTestPicker = lazy(() => import('./SatOfficialTest').then(m => ({ default: m.OfficialTestPicker })));
const OfficialAttemptView = lazy(() => import('./SatOfficialTest').then(m => ({ default: m.OfficialAttemptView })));
const OfficialTestEntry = lazy(() => import('./SatOfficialTest'));
const SatAdminCalibration = lazy(() => import('./SatAdminCalibration'));
const SatAdminAddQuestion = lazy(() => import('./SatAdminAddQuestion'));
const SatAdminExamBuilder = lazy(() => import('./SatAdminExamBuilder'));
const NotFound = lazy(() => import('./NotFound'));

// Login times and session durations for the admin dashboard: tracked on the SAT pages only.
function ActivityPing() {
  const { pathname } = useLocation();
  useActivityPing(pathname.startsWith('/tutoring/sat') && !pathname.startsWith('/tutoring/sat/admin'));
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider><CompanionProvider>
        <ActivityPing />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Hub />} />
            <Route path="/tutoring" element={<TutoringGate />} />
            <Route path="/tutoring/british-system" element={<Website />} />
            <Route path="/tutoring/sat" element={<SatAssessment />} />
            <Route path="/tutoring/sat/results/:token" element={<SatResultsPage />} />
            <Route path="/tutoring/sat/practice" element={<SatPracticeHub />} />
            <Route path="/tutoring/sat/practice/:domainSlug" element={<SatClassified />} />
            <Route path="/tutoring/sat/dashboard" element={<SatDashboard />} />
            <Route path="/tutoring/sat/path" element={<SatPath />} />
            <Route path="/tutoring/sat/path/:skillSlug" element={<SatPathSkill />} />
            <Route path="/tutoring/sat/start" element={<SatOnboarding />} />
            <Route path="/tutoring/sat/plan" element={<SatPlan />} />
            <Route path="/tutoring/sat/learn" element={<SatLearn />} />
            <Route path="/tutoring/sat/mastery-map" element={<SatMasteryMap />} />
            <Route path="/tutoring/sat/formulas" element={<SatFormulaReference />} />
            <Route path="/tutoring/sat/toolkit" element={<SatToolkit />} />
            <Route path="/tutoring/sat/progress" element={<SatProgress />} />
            <Route path="/tutoring/sat/learn/:skillSlug" element={<SatLesson />} />
            <Route path="/tutoring/sat/login" element={<SatLogin />} />
            <Route path="/tutoring/sat/forgot-password" element={<PPForgotPassword />} />
            <Route path="/tutoring/sat/reset-password" element={<PPResetPassword />} />
            <Route path="/tutoring/sat/terms" element={<PPTerms />} />
            <Route path="/tutoring/sat/privacy" element={<PPPrivacy />} />
            <Route path="/tutoring/sat/admin/reports" element={<SatAdminReports />} />
            <Route path="/tutoring/sat/admin/students" element={<SatAdminStudents />} />
            <Route path="/tutoring/sat/admin/applications" element={<SatAdminApplications />} />
            <Route path="/tutoring/sat/apply" element={<AcqApply />} />
            <Route path="/tutoring/sat/admin/calibration" element={<SatAdminCalibration />} />
            <Route path="/tutoring/sat/admin/add-question" element={<SatAdminAddQuestion />} />
            <Route path="/tutoring/sat/admin/exam-builder" element={<SatAdminExamBuilder />} />
            <Route path="/tutoring/sat/review" element={<SatReviewQuiz />} />
            <Route path="/tutoring/sat/exams" element={<SatExamLibrary />} />
            <Route path="/tutoring/sat/adaptive" element={<AdaptiveExamPicker />} />
            <Route path="/tutoring/sat/adaptive/:attemptId" element={<SatAdaptiveExamRunner />} />
            <Route path="/tutoring/sat/official" element={<OfficialTestPicker />} />
            <Route path="/tutoring/sat/official/attempt/:attemptId" element={<OfficialAttemptView />} />
            <Route path="/tutoring/sat/official/:testNumber/:section" element={<OfficialTestEntry />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/tutoring/british-system/admin" element={<Navigate to="/admin" replace />} />
            <Route path="/tutoring/british-system/portal" element={<StudentPortal />} />
            <Route path="/ratings" element={<CourseFeedback />} />
            <Route path="/gym" element={<Gym />} />
            <Route path="/gym/admin" element={<GymAdmin />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <CompanionOverlay />
        </CompanionProvider></ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
